import React, { useEffect, useMemo, useState } from "react";
import * as Mui from "@mui/material";
import {
  ArrowForwardRounded,
  ChevronLeftRounded,
  ChevronRightRounded,
  Groups2Rounded,
  InfoOutlined,
  PersonOffRounded,
  TodayRounded,
} from "@mui/icons-material";
import { designColors, designRadii } from "services/constants/designTokens";
import {
  ctaButtonSx,
  sectionCardSx,
  SectionHeader,
  ShiftCoverageCard,
} from "./homeUi";

const shiftPalette = [
  "#16A34A",
  "#2563EB",
  "#7C3AED",
  "#EA580C",
  "#0891B2",
  "#DB2777",
];
const visibleCardsPerPage = 3;

const buildShiftCards = (shiftDetails = []) =>
  shiftDetails.map((shift, index) => {
    const assigned = shift.assigned_members ?? 0;
    const present = shift.present_count ?? 0;
    const absent = shift.absent_count ?? 0;
    const late = shift.late_count ?? 0;
    const earlyExit = shift.early_exit_count ?? 0;
    const progress = assigned > 0 ? Math.round((present / assigned) * 100) : 0;

    return {
      id: shift.shift_id ?? `${shift.shift_name}-${index}`,
      title: shift.shift_name || "Shift",
      hours: `${assigned} assigned`,
      dotColor: shiftPalette[index % shiftPalette.length],
      progress,
      rows: [
        { label: "Assigned", value: assigned },
        { label: "Present", value: present },
        { label: "Absent", value: absent, alert: absent > 0 },
        { label: "Late", value: late, alert: late > 0 },
        { label: "Early Exit", value: earlyExit, alert: earlyExit > 0 },
      ],
      note:
        assigned > 0 && present === 0 && absent === 0
          ? "No check-in activity yet"
          : "",
    };
  });

const getAssignedShiftMembers = (shiftDetails = []) =>
  shiftDetails.reduce(
    (total, shift) => total + (Number(shift?.assigned_members) || 0),
    0,
  );

const buildShiftCoverageStats = (
  attendanceCounts,
  assignedShiftMembers,
  dayLabel,
) => [
  {
    label: "Assigned to Shifts",
    value: assignedShiftMembers,
    icon: Groups2Rounded,
    tint: "#EAFBF1",
    color: "#16A34A",
  },
  {
    label: "Week Off",
    value: attendanceCounts?.weekoff_count ?? 0,
    icon: TodayRounded,
    tint: "#F8FAFC",
    color: "#64748B",
  },
  {
    label: "Total Employees",
    value: attendanceCounts?.overall_count ?? 0,
    icon: Groups2Rounded,
    tint: "#ECFDF3",
    color: "#16A34A",
  },
  {
    label: `Absent ${dayLabel}`,
    value: attendanceCounts?.absent_count ?? 0,
    icon: PersonOffRounded,
    tint: "#FEF2F2",
    color: "#DC2626",
  },
];

export const ShiftCoverageSection = ({
  navigateToModule,
  attendanceCounts,
  dayLabel = "Today",
  dateSuffix = "Today",
  loading = false,
}) => {
  const isToday = dateSuffix === "Today";
  const shiftCards = useMemo(
    () => buildShiftCards(attendanceCounts?.shift_details || []),
    [attendanceCounts],
  );
  const assignedShiftMembers = useMemo(
    () =>
      getAssignedShiftMembers(attendanceCounts?.shift_details || []) ||
      (attendanceCounts?.planned_count ?? 0),
    [attendanceCounts],
  );
  const shiftCoverageStats = useMemo(
    () =>
      buildShiftCoverageStats(attendanceCounts, assignedShiftMembers, dayLabel),
    [attendanceCounts, assignedShiftMembers, dayLabel],
  );
  // assignedShiftMembers only sums shift_details, which the backend builds by
  // skipping any employee with no shift_id for the day (attendance_status_filter.py
  // build_shift_details) — so it plus weekoff_count under-counts overall_count by
  // exactly that dropped "no shift assigned" bucket (Shift Roster's own Unassigned
  // stat). Recovered here instead of trusting a third backend count, so this
  // sentence always reconciles with the overall_count the rest of the card uses.
  const unassignedMembers = Math.max(
    (attendanceCounts?.overall_count ?? 0) -
      assignedShiftMembers -
      (attendanceCounts?.weekoff_count ?? 0),
    0,
  );
  const pageCount = Math.max(
    1,
    Math.ceil(shiftCards.length / visibleCardsPerPage),
  );
  const [page, setPage] = useState(0);

  useEffect(() => {
    if (page > pageCount - 1) {
      setPage(Math.max(0, pageCount - 1));
    }
  }, [page, pageCount]);

  const visibleShiftCards = shiftCards.slice(
    page * visibleCardsPerPage,
    page * visibleCardsPerPage + visibleCardsPerPage,
  );

  return (
    <Mui.Box
      sx={{
        ...sectionCardSx,
        height: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {!loading && (
        <SectionHeader
          title={
            isToday
              ? "Today's Shift Coverage"
              : `Shift Coverage (${dateSuffix})`
          }
          tooltip="How many team members are assigned to each shift, and how many have checked in against it, for the selected day."
          endAdornment={
            shiftCards.length > visibleCardsPerPage ? (
              <Mui.Stack direction="row" alignItems="center" spacing={0.2}>
                <Mui.IconButton
                  size="small"
                  onClick={() => setPage((prev) => Math.max(0, prev - 1))}
                  disabled={page === 0}
                  sx={{ p: 0.35 }}
                >
                  <ChevronLeftRounded sx={{ fontSize: 18 }} />
                </Mui.IconButton>
                <Mui.Typography
                  sx={{ fontSize: 12, color: designColors.slate600 }}
                >
                  {page + 1}/{pageCount}
                </Mui.Typography>
                <Mui.IconButton
                  size="small"
                  onClick={() =>
                    setPage((prev) => Math.min(pageCount - 1, prev + 1))
                  }
                  disabled={page === pageCount - 1}
                  sx={{ p: 0.35 }}
                >
                  <ChevronRightRounded sx={{ fontSize: 18 }} />
                </Mui.IconButton>
              </Mui.Stack>
            ) : null
          }
        />
      )}

      {loading ? (
        <>
          <Mui.Box sx={{ flex: 1, pt: 1.1 }}>
            <Mui.Skeleton
              variant="rounded"
              height={44}
              sx={{ borderRadius: 2.5, mb: 1.1 }}
            />
            <Mui.Grid container spacing={1.5}>
              {Array.from({ length: 3 }).map((_, index) => (
                <Mui.Grid item xs={12} md={4} key={index}>
                  <Mui.Skeleton
                    variant="rounded"
                    height={220}
                    sx={{ borderRadius: 2.5 }}
                  />
                </Mui.Grid>
              ))}
            </Mui.Grid>
            <Mui.Grid container spacing={1.5} sx={{ mt: 0.9 }}>
              {Array.from({ length: 4 }).map((_, index) => (
                <Mui.Grid item xs={12} sm={6} key={index}>
                  <Mui.Skeleton
                    variant="rounded"
                    height={74}
                    sx={{ borderRadius: 2.5 }}
                  />
                </Mui.Grid>
              ))}
            </Mui.Grid>
          </Mui.Box>
          <Mui.Stack
            direction={{ xs: "column", sm: "row" }}
            justifyContent="space-between"
            spacing={1.5}
            alignItems={{ xs: "stretch", sm: "center" }}
            sx={{ mt: 1.25 }}
          >
            <Mui.Skeleton variant="text" width="58%" height={22} />
            <Mui.Skeleton
              variant="rounded"
              width={154}
              height={36}
              sx={{ borderRadius: 999 }}
            />
          </Mui.Stack>
        </>
      ) : (
        <>
          <Mui.Box sx={{ flex: 1 }}>
            <Mui.Box
              sx={{
                mt: 1.1,
                mb: 1.1,
                px: 1.5,
                py: 0.85,
                borderRadius: designRadii.card,
                border: `1px solid #CBE7D5`,
                backgroundColor: "#F7FDF9",
              }}
            >
              <Mui.Stack direction="row" spacing={1} alignItems="center">
                <InfoOutlined sx={{ fontSize: 17, color: "#15803D" }} />
                <Mui.Typography
                  sx={{ fontSize: 13.5, color: designColors.slate700 }}
                >
                  Showing{" "}
                  {Math.min(
                    visibleCardsPerPage,
                    shiftCards.length || visibleCardsPerPage,
                  )}{" "}
                  shifts at a time for {isToday ? "today" : dateSuffix}.
                </Mui.Typography>
              </Mui.Stack>
            </Mui.Box>

            {shiftCards.length > 0 ? (
              <Mui.Grid container spacing={1.5}>
                {visibleShiftCards.map((item) => (
                  <Mui.Grid item xs={12} md={4} key={item.id}>
                    <ShiftCoverageCard
                      title={item.title}
                      hours={item.hours}
                      dotColor={item.dotColor}
                      progress={item.progress}
                      rows={item.rows}
                      note={item.note}
                    />
                  </Mui.Grid>
                ))}
              </Mui.Grid>
            ) : (
              <Mui.Box
                sx={{
                  border: `1px dashed ${designColors.slate200}`,
                  borderRadius: designRadii.card,
                  px: 2,
                  py: 3,
                  textAlign: "center",
                  backgroundColor: designColors.white,
                }}
              >
                <Mui.Typography
                  sx={{
                    fontSize: 14,
                    fontWeight: 700,
                    color: designColors.slate800,
                  }}
                >
                  No shift details available
                </Mui.Typography>
                <Mui.Typography
                  sx={{
                    fontSize: 12.75,
                    color: designColors.slate600,
                    mt: 0.4,
                  }}
                >
                  Shift coverage cards will appear here when `shift_details` is
                  returned.
                </Mui.Typography>
              </Mui.Box>
            )}

            <Mui.Grid container spacing={1.5} sx={{ mt: 0.9 }}>
              {shiftCoverageStats.map((item) => (
                <Mui.Grid item xs={12} sm={6} key={item.label}>
                  <Mui.Box
                    sx={{
                      border: `1px solid ${designColors.slate200}`,
                      borderRadius: designRadii.card,
                      p: 1.4,
                      backgroundColor: designColors.white,
                      display: "flex",
                      alignItems: "center",
                      gap: 1.25,
                    }}
                  >
                    <Mui.Box
                      sx={{
                        width: 38,
                        height: 38,
                        borderRadius: 1.5,
                        backgroundColor: item.tint,
                        color: item.color,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <item.icon sx={{ fontSize: 20 }} />
                    </Mui.Box>
                    <Mui.Box>
                      <Mui.Typography
                        sx={{
                          fontSize: 13.5,
                          fontWeight: 600,
                          color: designColors.slate700,
                        }}
                      >
                        {item.label}
                      </Mui.Typography>
                      <Mui.Typography
                        sx={{
                          fontSize: 18,
                          fontWeight: 700,
                          color: item.color,
                        }}
                      >
                        {item.value}
                      </Mui.Typography>
                    </Mui.Box>
                  </Mui.Box>
                </Mui.Grid>
              ))}
            </Mui.Grid>
          </Mui.Box>

          <Mui.Stack
            direction={{ xs: "column", sm: "row" }}
            justifyContent="space-between"
            spacing={1.5}
            alignItems={{ xs: "stretch", sm: "center" }}
            sx={{ mt: 1.25 }}
          >
            <Mui.Typography
              sx={{ fontSize: 13.5, color: designColors.slate700 }}
            >
              {assignedShiftMembers} assigned to shifts +{" "}
              {attendanceCounts?.weekoff_count ?? 0} on weekly off
              {unassignedMembers > 0
                ? ` + ${unassignedMembers} unassigned`
                : ""}{" "}
              = {attendanceCounts?.overall_count ?? 0} total employees.
            </Mui.Typography>
            <Mui.Button
              endIcon={<ArrowForwardRounded />}
              sx={ctaButtonSx}
              onClick={() => navigateToModule("Rosters")}
            >
              View Full Roster
            </Mui.Button>
          </Mui.Stack>
        </>
      )}
    </Mui.Box>
  );
};
